import { ReadonlyURLSearchParams } from "next/navigation";

import { pageConfig } from "@/config/pages.config";
import { DealType } from "@/enums/dealType";
import { DEFAULT_SORT_VALUE, SORT_KEY } from "@/ui/SearchResultHeader";

/**
 * Получает базовый URL для конкретного типа сделки
 * @param dealType Тип сделки (первичная, покупка, аренда)
 * @returns Базовый URL для указанного типа сделки
 */
export const getRedirectUrl = (dealType?: string | null) => {
  switch (dealType) {
    case DealType.PRIMARY:
      return pageConfig.developers;
    case DealType.BUY:
      return pageConfig.buy;
    case DealType.RENT:
      return pageConfig.rent;
    default:
      return pageConfig.developers;
  }
};

/**
 * Получает полный URL для объекта недвижимости с учетом слага
 * @param slug Слаг объекта недвижимости
 * @param dealType Тип сделки (первичная, покупка, аренда)
 * @param locale Локаль (опционально)
 * @returns Полный URL для объекта недвижимости
 */
export const getPropertyUrl = (
  slug: string,
  dealType: string | null = DealType.PRIMARY,
  locale?: string,
) => {
  const baseUrl = getRedirectUrl(dealType);
  const localePrefix = locale ? `/${locale}` : "";

  return `${localePrefix}${baseUrl}/${slug}/`;
};

export const getRedirectUrlWithParams = (
  dealType: DealType,
  searchParams: ReadonlyURLSearchParams,
) => {
  const baseUrl = getRedirectUrl(dealType) + "/";
  const currentParams = new URLSearchParams();

  // Preserve only relevant params
  const limit = searchParams.get("limit");
  const sort = searchParams.get("sort") || localStorage.getItem(SORT_KEY);

  if (sort === DEFAULT_SORT_VALUE) {
    if (dealType) currentParams.delete("dealType");
    if (limit) currentParams.delete("limit");
    if (sort) currentParams.delete("sort");
  } else if (sort !== DEFAULT_SORT_VALUE) {
    if (dealType) currentParams.set("dealType", dealType);
    if (limit) currentParams.set("limit", limit);
    if (sort) currentParams.set("sort", sort);
  }

  // If there are params, append them to the URL
  const queryString = currentParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

export const normalizePathByDealType = (
  path: string,
  isCatalogPage: boolean,
): string => {
  const splitPath = path.split("/").filter(Boolean);

  const pageIndex = splitPath.indexOf("page");
  const normalizedPath =
    pageIndex >= 0 ? splitPath.slice(0, pageIndex) : splitPath;

  const isDealType = (value: string): value is DealType =>
    value === DealType.PRIMARY ||
    value === DealType.BUY ||
    value === DealType.RENT;

  const dealTypeIndex = normalizedPath.findIndex(isDealType);

  let finalPath = normalizedPath;

  if (isCatalogPage && dealTypeIndex !== -1) {
    const isPrimary = normalizedPath[dealTypeIndex] === DealType.PRIMARY;
    finalPath = normalizedPath.slice(0, dealTypeIndex + (isPrimary ? 0 : 1));
  }

  return `/${finalPath.join("/")}`;
};
